import React from 'react';
import { Handle, Position } from '@xyflow/react';
import type { Node, NodeProps } from '@xyflow/react';

/**
 * Exit Node Component
 *
 * Represents the exit point from a nested child graph. Exit nodes are special
 * marker nodes that are automatically created when a container node's child graph
 * is initialized. They serve as the source for connections heading back to the
 * parent graph through the container's output pins.
 *
 * **Design:**
 * - Slim rectangular marker node positioned on the right side of the canvas
 * - Always has exactly one input handle (to receive connections from within child graph)
 * - Number of Exit nodes matches the number of output pins on the parent container
 * - Non-deletable while it has incoming connections (enforced by the API)
 * - Read-only node (cannot edit properties)
 *
 * **Semantics:**
 * When traversal reaches an Exit node j inside a child graph, execution resumes
 * at the container's output pin j in the parent graph. This enables the child graph
 * to branch into multiple different parent-level paths.
 *
 * **Example Use Case:**
 * A dialogue container has 3 outputs (Continue, Goodbye, Combat). Inside the
 * container, 3 Exit nodes are shown on the right. When dialogue ends, it routes
 * to one of these Exit nodes, which connects back to the appropriate parent path.
 *
 * **Pin Removal:**
 * When removing an output pin from a container, the corresponding Exit node is
 * deleted. If that Exit has incoming connections, the API shows a warning
 * ("Cannot remove exit: 2 connections would be deleted") and blocks the operation.
 *
 * @component
 * @example
 * // Automatically created by the system when opening a container
 * const exitNode: Node = {
 *   id: 'exit_0',
 *   type: 'exit',
 *   position: { x: 800, y: 0 },
 *   data: { index: 0 },
 * };
 */
export const ExitNode: React.FC<NodeProps> = ({ data }) => {
    const index = data?.index ?? 0;

    return (
        <div className="exit-node">
            {/* Input handle: receives connections from internal graph nodes */}
            <Handle type="target" position={Position.Left} id="in" />

            {/* Visual indicator */}
            <div className="exit-node-content">
                <span className="exit-node-label">Exit {index}</span>
            </div>
        </div>
    );
};

/**
 * Exit Node Metadata
 *
 * Defines the schema, display properties, and default data for Exit nodes.
 * This metadata is used by the NodeTypeRegistry to register the node type
 * and make it available in the system.
 *
 * @constant
 */
export const exitNodeMeta = {
    type: 'exit' as const,
    label: 'Exit',
    icon: '↴',
    defaultData: {
        index: 0,
    },
    description: 'Exit point for a nested graph (auto-created)',
    category: 'flow-control',
};
